// The lead-sheet song model (used by the built-in presets) and the flat
// SequenceChord the optimiser and diagram renderer consume. The live pipeline
// is songToChart → chartToSequence in chart.ts — the old flat-Song runtime
// helpers (flattenSong, playbackOrder, display lanes…) were dead code and have
// been removed; they also hardcoded 4-beat bars, so don't revive them for the
// mixed-meter chart model.

import type { GuitarGripPin } from './voicingPins';
import { type ChordTypeId } from './chords';
import type { StringSet } from './tuning';

export interface ChordEvent {
  /** Canonical authored harmony id, retained for score-editor hit testing. */
  sourceId?: string;
  beat: number;
  duration: number; // in beats
  root: string;
  chordType: ChordTypeId;
  /**
   * Display symbol as the source wrote it ("G7/D", "Am", "B7alt"), when it
   * differs from the catalogue type's own label. The engraver prefers this;
   * chordType stays the playable four-part mapping.
   */
  symbol?: string;
  targetTopNote?: string;
  preferredInversion?: number;
  /** The slash bass as written ("C" in "D/C"), when the symbol names one. */
  bass?: string;
}

/**
 * A harmony mark that belongs on the printed score but has no playable
 * pitches. Explicit N.C. spans use this lane so engraving can retain their
 * onset and width without exposing a fake chord to voicing or audio code.
 */
export interface HarmonyLabelEvent {
  /** Canonical authored harmony id, retained for score-editor hit testing. */
  sourceId?: string;
  beat: number;
  duration: number;
  text: string;
}

/**
 * A pitched, rhythmic note in the melody voice of a bar. The melody line is
 * what turns a chord chart into a lead sheet, and feeds the chord-melody
 * arranger as per-beat target top notes (see arrangementSequence). Unlike
 * ChordEvent.targetTopNote it carries an octave (MIDI) and a duration.
 */
export interface MelodyNote {
  /** Index in the authored event; engraving order can change after repitching. */
  sourceHeadIndex?: number;
  id: string;
  /** Absolute pitch as MIDI number (C4 = middle C = 60). */
  midi: number;
  /**
   * Optional explicit spelling ("Eb" vs "D#"). Spelling decides the staff
   * line, so it matters for engraving; when omitted, spell from the song key.
   */
  spelledAs?: string;
  beat: number; // onset within the bar (1-based, like ChordEvent.beat)
  duration: number; // in beats
  /**
   * Notated length in beats before tuplet scaling. `duration` remains the
   * sounding length used by playback, onset arithmetic and spacing; this
   * optional value lets the engraver choose the written glyph, dots and beam
   * count for tuplets whose sounding length cannot identify that value (for
   * example a 5:4 written sixteenth sounds for 1/5 of a quarter).
   */
  writtenDuration?: number;
  tie?: 'start' | 'stop' | 'both';
  /** A rest occupies time but draws no notehead. */
  rest?: boolean;
  /** Unpitched improvisation mark; midi is ignored for notation and playback. */
  slash?: 'beat' | 'rhythm';
  /**
   * A grace note: engraved small before its principal (the next real note),
   * occupying no musical time. `beat` carries a small visual pre-offset so
   * spacing puts it left of the principal; playback skips it.
   */
  grace?: boolean;
  /** Cue-size note: full rhythmic value, engraved small. */
  cue?: boolean;
  /**
   * Written tuplet size (TupletGroup.actual — 6 for a sextuplet), when the
   * note came from an explicit tuplet group. The engraver prefers this for
   * the group figure: a rest-led sextuplet beams five noteheads but is
   * still a "6".
   */
  tuplet?: number;
  /**
   * Identity of the written tuplet group this note belongs to, so the
   * engraver can tell two ADJACENT groups apart (back-to-back quarter
   * triplets are time-contiguous but draw two brackets, not one).
   */
  tupletGroup?: string;
  /** Articulations, engraved on the notehead side opposite the stem. */
  articulations?: ('staccato' | 'accent' | 'tenuto' | 'marcato')[];
  /** Ornament symbols engraved above the note (tr, turn, mordent…); a
   *  measure-level fermata rides the bar's last note through this channel. */
  ornaments?: ('trill' | 'turn' | 'inverted-turn' | 'mordent' | 'inverted-mordent' | 'fermata')[];
  /** Dynamic mark sounding from this note ("pp"…), engraved below the staff. */
  dynamic?: string;
  /** Staff text printed above this note's column ("Ob. Viol.", "dolce"). */
  words?: string;
  /** Staff text printed below this note's column (a below-placed cue). */
  wordsBelow?: string;
  /** Sung syllables by verse index (imported vocal lines). */
  lyrics?: ({ text: string; hyphen?: boolean } | undefined)[];
  /**
   * Additional simultaneous noteheads BELOW the top one — a chord on one stem
   * (piano LH, chord-melody stacks). The top notehead stays midi/spelledAs so
   * every single-line consumer (lead-sheet projection, chord-melody targets)
   * keeps reading the lead untouched. Any order; the engraver sorts.
   */
  chordNotes?: {
    midi: number;
    spelledAs?: string;
    tie?: MelodyNote['tie'];
    sourceHeadIndex?: number;
  }[];
}

export interface Bar {
  id: string;
  beats: number;
  /** The meter this bar's beats count in, when it differs from
   *  song.timeSignature — beats and durations are in ITS denominator. */
  timeSig?: [number, number];
  chords: ChordEvent[];
  /** Display-only harmony marks (currently explicit N.C. spans). */
  harmonyLabels?: HarmonyLabelEvent[];
  /** Opening barline decoration (forward repeat / double), from the form. */
  openBarline?: 'repeat' | 'double';
  /** Volta bracket starting at this bar (1, 2, …; 0 = un-numbered). */
  ending?: number;
  /** The bar's written content exceeds its meter — a legal editing state
   * (Guitar Pro model): drawn as an error mark until the durations add up. */
  overfull?: boolean;
  /** Conventional one- or two-bar repeat sign in an otherwise empty measure. */
  barRepeat?: 1 | 2;
  /** Form-level text placed at the start of the measure. */
  staffText?: string;
  /** Place form-level staff text above the staff; below when unset/false. */
  staffTextAbove?: boolean;
  /** Navigation directive printed over the bar (D.S. al Coda, Fine, …). */
  directive?: string;
  /**
   * Authored Feel cue label taking effect at this bar ("Latin", "Swing").
   * A musical direction, printed like one — never a groove or loop name.
   */
  feel?: string;
  segno?: boolean;
  coda?: boolean;
  /** Closing barline decoration; unset = positional default (single/final). */
  closeBarline?: 'repeat' | 'double' | 'final';
  /** Optional melody voice. Undefined = a chord-only chart (legacy behaviour). */
  melody?: MelodyNote[];
  /** Optional second voice (engraved stems-down beneath the melody). */
  melody2?: MelodyNote[];
  /**
   * Voices 3+ (Telemann's ensemble staves carry three). Kept out of
   * melody/melody2 so every existing two-voice consumer reads unchanged;
   * N-voice consumers go through barVoices().
   */
  melodyMore?: MelodyNote[][];
}

/**
 * ALL of a bar's voices in voice order — the one place that knows the render
 * model stores them as melody/melody2/melodyMore. Trailing empty voices are
 * trimmed; index 0 always exists (a chord-only bar is one empty voice).
 */
/**
 * The song with every sung syllable removed — the lyric-toggle projection.
 * Spacing follows for free: without lyrics, syllable widths stop driving
 * note advances and the verse lanes fold out of the system stack.
 */
export function songWithoutLyrics(song: Song): Song {
  const strip = (n: MelodyNote): MelodyNote => {
    if (!n.lyrics) return n;
    const { lyrics, ...rest } = n;
    void lyrics;
    return rest;
  };
  return {
    ...song,
    sections: song.sections.map((sec) => ({
      ...sec,
      bars: sec.bars.map((bar) => ({
        ...bar,
        ...(bar.melody ? { melody: bar.melody.map(strip) } : {}),
        ...(bar.melody2 ? { melody2: bar.melody2.map(strip) } : {}),
        ...(bar.melodyMore ? { melodyMore: bar.melodyMore.map((v) => v.map(strip)) } : {}),
      })),
    })),
  };
}

/**
 * The song with the chord-symbol lane emptied.
 *
 * The arrangement lenses draw a grip per chord with the symbol above it, so
 * the engraved lane would be a second symbol for the same chord. Removing the
 * chords rather than hiding them also shortens `aboveNeeds`, which is what
 * lets the grips sit close to the staff instead of floating above a reserved
 * band nothing is drawn in.
 */

/** Strip the chord-symbol lane. In a stacked full score the harmony lane
 * belongs above the TOP staff only — every lower staff engraves without it. */
export function songWithoutHarmony(song: Song): Song {
  return {
    ...song,
    sections: song.sections.map((section) => ({
      ...section,
      bars: section.bars.map((bar) => {
        const next = { ...bar, chords: [] };
        delete next.harmonyLabels;
        return next;
      }),
    })),
  };
}

/** Lower staves in a full score share the top staff's conductor directions.
 * Keep their own note text and the barline/repeat structure intact. A part
 * viewed alone uses its unstripped projection, including every direction. */
export function songForLowerStaff(song: Song): Song {
  const withoutHarmony = songWithoutHarmony(song);
  return {
    ...withoutHarmony,
    sections: withoutHarmony.sections.map((section) => ({
      ...section,
      bars: section.bars.map((bar) => {
        const next = { ...bar };
        delete next.staffText;
        delete next.staffTextAbove;
        delete next.directive;
        return next;
      }),
    })),
  };
}

export function songWithoutChordSymbols(song: Song): Song {
  return {
    ...song,
    sections: song.sections.map((sec) => ({
      ...sec,
      bars: sec.bars.map((bar) => (bar.chords?.length ? { ...bar, chords: [] } : bar)),
    })),
  };
}

export function barVoices(bar: Pick<Bar, 'melody' | 'melody2' | 'melodyMore'>): MelodyNote[][] {
  const voices = [bar.melody ?? [], bar.melody2 ?? [], ...(bar.melodyMore ?? [])];
  while (voices.length > 1 && voices[voices.length - 1].length === 0) voices.pop();
  return voices;
}

export interface Section {
  id: string;
  name: string;
  bars: Bar[];
}

/** How a track's notes are drawn. */
export type StaffType = 'standard' | 'tab';

/**
 * An instrument part. A Song is really a set of tracks; the single-melody
 * `sections` model is the degenerate one-track case. Tracks unlock piano
 * (grand staff), guitar tab, and multi-instrument scores. Additive: when
 * `Song.tracks` is absent, the sections are treated as one standard track.
 */
export interface Track {
  id: string;
  name: string;
  clef?: 'treble' | 'bass';
  staffType?: StaffType;
  /** MIDI of open strings, low→high, for a tab staff (e.g. standard guitar). */
  tuning?: number[];
  bars: Bar[];
}

export interface Song {
  id: string;
  title: string;
  key?: string;
  timeSignature: [number, number];
  sections: Section[];
  /** Optional multi-track representation; when present it supersedes sections. */
  tracks?: Track[];
  /**
   * Slur arcs between rendered melody notes, referenced by MelodyNote id
   * (render model, projected from Score spanners). Slurs routinely cross
   * barlines and systems, so they live on the song, not on notes or bars.
   */
  slurs?: { fromId: string; toId: string; side?: 'above' | 'below' }[];
  /** Tempo/expression words above the first system ("Sehr langsam"). */
  tempoText?: string;
  /** Metronome mark printed beside the tempo words ("Allegro (♩ = 130)"). */
  tempoBpm?: number;
  /** Crescendo/diminuendo wedges between rendered notes (render model,
   *  projected from Score spanners; drawn in the dynamics lane). */
  hairpins?: { fromId: string; toId: string; kind: 'cresc' | 'dim' }[];
}

/**
 * The tracks to render for a song. Uses `song.tracks` when present, otherwise
 * synthesizes a single standard treble track from the section bars — so every
 * existing single-melody song keeps working unchanged.
 */
export function resolveTracks(song: Song): Track[] {
  if (song.tracks && song.tracks.length > 0) return song.tracks;
  return [
    {
      id: 'melody',
      name: 'Melody',
      clef: 'treble',
      staffType: 'standard',
      bars: song.sections.flatMap((s) => s.bars),
    },
  ];
}

/** Flat runtime chord used by the chart renderer and the optimiser. */
export interface SequenceChord {
  id: string;
  /** Engine-internal root (sharp spelling). */
  root: string;
  /** Root as written in the chart (may be a flat). */
  displayRoot: string;
  chordType: ChordTypeId;
  symbol: string;
  barIndex: number;
  beat: number;
  durationBeats: number;
  /** The chord's span as written in its own bar (the chart chord's `beats`).
   *  `durationBeats` is how long it SOUNDS, which runs on through following
   *  empty bars; a surface laying the bar out by beats reads this instead. */
  writtenBeats?: number;
  stringSet: StringSet;
  targetTopNote?: string;
  preferredInversion?: number;
  sectionId?: string;
  barId?: string;
  /** The slash bass as written ("C" in "D/C"), when the symbol names one.
   *  A slash chord names its own bottom note, so whatever plays the bottom
   *  plays it: the walking line anchors its downbeat there and the piano's
   *  left hand states it instead of the root. */
  bass?: string;
  /** The chord's position key in the comping lane (voicingPins.ts). */
  pinKey?: string;
  /** The user's pinned grip for it, when one applies. */
  pin?: GuitarGripPin | null;
}

/**
 * Collision-free unique id for runtime chord events and saved charts.
 * Uses crypto.randomUUID where available so ids never clash with persisted
 * ids after a page reload (a counter would reset to 0 and reissue live ids).
 */
export function uid(prefix = 'chord'): string {
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefix}-${rand}`;
}

export function createEmptySong(): Song {
  return {
    id: uid('song'),
    title: 'Untitled Chart',
    key: 'C',
    timeSignature: [4, 4],
    sections: [{ id: 'main', name: 'Main', bars: [{ id: 'b1', beats: 4, chords: [] }] }],
  };
}
